import { supabase } from '../lib/supabase';

const EVIDENCE_BUCKET = 'assessment-evidence';
const FILE_MIME_TYPES = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  txt: 'text/plain',
};

const normalizeEvidencePath = value => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  let decoded = raw;
  // Evidence rows created by older builds may contain the bucket prefix or a
  // complete Supabase Storage URL. Storage methods expect only the object
  // name, so reduce all of those representations to the same path.
  try { decoded = decodeURIComponent(raw); } catch (_) { /* keep the original path */ }
  if (/^https?:\/\//i.test(decoded)) {
    try {
      const pathname = new URL(decoded).pathname;
      const marker = pathname.toLowerCase().indexOf('/storage/v1/object/');
      if (marker >= 0) {
        decoded = pathname.slice(marker + '/storage/v1/object/'.length)
          .replace(/^(public|authenticated|sign|download)\//i, '');
      }
    } catch (_) { /* keep the original URL */ }
  }
  return decoded
    .replace(/^\/+/, '')
    .replace(new RegExp(`^${EVIDENCE_BUCKET}/`, 'i'), '');
};

const isSupabaseStorageUrl = value => /\/storage\/v1\/object\//i.test(String(value || ''));

const createInlineObjectUrl = (sourceBlob, filePath) => {
  const extension = normalizeEvidencePath(filePath).split('?')[0].split('.').pop()?.toLowerCase();
  const mimeType = FILE_MIME_TYPES[extension] || sourceBlob.type || 'application/octet-stream';
  return URL.createObjectURL(new Blob([sourceBlob], { type: mimeType }));
};

const getServerPreviewUrl = async filePath => {
  const { data, error } = await supabase.functions.invoke('view-poe-evidence', {
    body: { filePath },
  });
  if (error) {
    let detail = '';
    try {
      const response = error?.context;
      if (response?.clone) {
        const payload = await response.clone().json();
        detail = payload?.error || payload?.message || '';
      }
    } catch (_) { /* keep the SDK error */ }
    throw new Error(detail || error.message || 'The secure POE preview could not be opened.');
  }
  if (!(data instanceof Blob) || !data.size) throw new Error('The server returned an empty POE file.');
  return createInlineObjectUrl(data, filePath);
};

const throwIfError = error => {
  if (error) throw error;
};

// Older deployments of get_trainer_poe_dashboard return the unit and student
// rows without the registration period columns. Resolve those periods from
// the underlying registration tables so the client-side filters still act on
// the same enrolment record as the marks/evidence row.
const attachRegistrationPeriods = async rows => {
  const sourceRows = Array.isArray(rows) ? rows : [];
  const needsPeriod = sourceRows.filter(row => !row?.academic_year_id || !row?.semester_id);
  if (!needsPeriod.length) return sourceRows;

  const assessmentIds = [...new Set(needsPeriod.map(row => row.assessment_id).filter(Boolean).map(String))];
  const assessmentRegistrations = new Map();
  if (assessmentIds.length) {
    const { data, error } = await supabase
      .from('student_assessments')
      .select('id, unit_registration_id')
      .in('id', assessmentIds);
    if (!error) (data || []).forEach(row => assessmentRegistrations.set(String(row.id), row.unit_registration_id));
  }

  const registrationIds = [...new Set([
    ...needsPeriod.map(row => row.unit_registration_id).filter(Boolean),
    ...assessmentRegistrations.values(),
  ].map(String))];
  const studentIds = [...new Set(needsPeriod.map(row => row.student_id).filter(Boolean).map(String))];
  const unitIds = [...new Set(needsPeriod.map(row => row.unit_id).filter(Boolean).map(String))];
  const registrations = [];

  const selectColumns = 'id,student_id,unit_id,semester_enrollment_id,academic_year_id,semester_id,enrolled_at,created_at';
  if (registrationIds.length) {
    const { data, error } = await supabase
      .from('student_unit_registrations')
      .select(selectColumns)
      .in('id', registrationIds);
    if (!error) registrations.push(...(data || []));
  }

  // Rows without marks do not carry an assessment id. Resolve their current
  // registration by student + unit, preferring the most recently enrolled row.
  if (studentIds.length && unitIds.length) {
    const { data, error } = await supabase
      .from('student_unit_registrations')
      .select(selectColumns)
      .in('student_id', studentIds)
      .in('unit_id', unitIds)
      .order('enrolled_at', { ascending: false });
    if (!error) registrations.push(...(data || []));
  }

  const enrollmentIds = [...new Set(registrations.map(row => row.semester_enrollment_id).filter(Boolean).map(String))];
  const enrollments = new Map();
  if (enrollmentIds.length) {
    const { data, error } = await supabase
      .from('student_semester_enrollments')
      .select('id,academic_year_id,semester_id,status,updated_at,created_at')
      .in('id', enrollmentIds);
    if (!error) (data || []).forEach(row => enrollments.set(String(row.id), row));
  }

  const byId = new Map();
  const byStudentUnit = new Map();
  registrations.forEach(row => {
    byId.set(String(row.id), row);
    const key = `${row.student_id}:${row.unit_id}`;
    const existing = byStudentUnit.get(key);
    if (!existing || String(row.enrolled_at || row.created_at || '') > String(existing.enrolled_at || existing.created_at || '')) {
      byStudentUnit.set(key, row);
    }
  });

  return sourceRows.map(row => {
    if (row?.academic_year_id && row?.semester_id) return row;
    const assessmentRegistrationId = row.assessment_id ? assessmentRegistrations.get(String(row.assessment_id)) : null;
    const registration = (row.unit_registration_id && byId.get(String(row.unit_registration_id)))
      || (assessmentRegistrationId && byId.get(String(assessmentRegistrationId)))
      || byStudentUnit.get(`${row.student_id}:${row.unit_id}`);
    if (!registration) return row;
    const enrollment = registration.semester_enrollment_id
      ? enrollments.get(String(registration.semester_enrollment_id))
      : null;
    return {
      ...row,
      academic_year_id: row.academic_year_id || enrollment?.academic_year_id || registration.academic_year_id || null,
      semester_id: row.semester_id || enrollment?.semester_id || registration.semester_id || null,
    };
  });
};

export const poeService = {
  async getItems() {
    const { data, error } = await supabase.rpc('get_poe_workflow_items');
    throwIfError(error);
    return data || [];
  },

  async getExternalDashboardItems() {
    const { data, error } = await supabase.rpc('get_external_poe_dashboard');
    throwIfError(error);
    return data || [];
  },

  async getExternalFilterOptions() {
    const { data, error } = await supabase.rpc('get_external_poe_filter_options');
    // Do not fall back to direct table reads here. The catalogue is
    // institution-wide and must be guarded by the external-verifier RPC;
    // bypassing it would allow another authenticated role to enumerate the
    // institution even when the RPC correctly rejects that role.
    throwIfError(error);
    return data || {
      programmes: [],
      departments: [],
      units: [],
      cams: [],
      years: [],
      semesters: [],
    };
  },

  async getExternalProgrammes(departmentId = null) {
    const { data, error } = await supabase.rpc('get_external_programmes', {
      p_department_id: departmentId || null,
    });
    throwIfError(error);
    return data || [];
  },

  async getStudentDashboardItems() {
    const { data, error } = await supabase.rpc('get_student_poe_dashboard_v2');
    throwIfError(error);
    return data || [];
  },

  async getTrainerDashboardItems() {
    const { data, error } = await supabase.rpc('get_trainer_poe_dashboard');
    throwIfError(error);
    return attachRegistrationPeriods(data || []);
  },

  async uploadMarkedScript({ assessmentId, file, note = '' }) {
    const { data: authData, error: authError } = await supabase.auth.getUser();
    throwIfError(authError);
    const userId = authData?.user?.id;
    if (!userId) throw new Error('Authentication required');
    if (!assessmentId || !file) throw new Error('Select a marked CAM and scanned script');

    const extension = String(file.name || '').split('.').pop()?.toLowerCase() || 'pdf';
    const safeExtension = extension.replace(/[^a-z0-9]/g, '') || 'pdf';
    const filePath = `${userId}/evidence/${assessmentId}/${Date.now()}.${safeExtension}`;
    const { data: uploaded, error: uploadError } = await supabase.storage
      .from(EVIDENCE_BUCKET)
      .upload(filePath, file, { upsert: false, contentType: file.type || undefined });
    throwIfError(uploadError);

    const { data, error } = await supabase.rpc('submit_poe_evidence', {
      p_student_assessment_id: assessmentId,
      p_file_path: uploaded.path,
      p_file_name: file.name,
      p_file_size: file.size,
      p_mime_type: file.type || null,
      p_student_note: note || null
    });

    if (error) {
      await supabase.storage.from(EVIDENCE_BUCKET).remove([uploaded.path]);
      throw error;
    }
    return data;
  },

  async review({ evidenceId, action, comments = '' }) {
    const { data, error } = await supabase.rpc('review_poe_evidence', {
      p_evidence_id: evidenceId,
      p_action: action,
      p_comments: comments || null
    });
    throwIfError(error);
    return data;
  },

  async verifyExternally({ evidenceId, comments = '' }) {
    const { data, error } = await supabase.rpc('verify_poe_externally', {
      p_evidence_id: evidenceId,
      p_comments: comments || null,
    });
    throwIfError(error);
    return data;
  },

  async declineExternally({ evidenceId, comments = '' }) {
    const { data, error } = await supabase.rpc('decline_poe_externally', {
      p_evidence_id: evidenceId,
      p_comments: comments || null,
    });
    throwIfError(error);
    return data;
  },

  async getFileUrl(filePath) {
    if (!filePath) return '';
    // Some records may already contain a public/signed URL. Do not pass
    // those back through Storage as if they were object paths.
    if (/^https?:\/\//i.test(String(filePath)) && !isSupabaseStorageUrl(filePath)) return String(filePath);
    const originalPath = String(filePath).trim();
    const normalizedPath = normalizeEvidencePath(originalPath);
    const candidatePaths = [...new Set([
      isSupabaseStorageUrl(originalPath) ? normalizedPath : originalPath,
      normalizedPath,
    ].filter(path => path && !/^https?:\/\//i.test(path)))];
    let lastError = null;
    for (const candidatePath of candidatePaths) {
      const { data, error } = await supabase.storage
        .from(EVIDENCE_BUCKET)
        .createSignedUrl(candidatePath, 900);
      if (!error && data?.signedUrl) return data.signedUrl;
      lastError = error;
    }
    if (lastError) {
      const message = String(lastError.message || '').toLowerCase();
      if (message.includes('object not found') || lastError.status === 404 || lastError.statusCode === '404') {
        throw new Error('The uploaded POE file could not be accessed. It may be missing from storage or the current role may not have permission to view it.');
      }
      throw lastError;
    }
    return '';
  },

  async getFilePreviewUrl(filePath, { stamped = false } = {}) {
    if (!filePath) return '';
    if (/^(blob|data):/i.test(String(filePath))) return String(filePath);

    // External POE verification must use the server-rendered copy. The Edge
    // Function reads the private object with the service role and embeds the
    // verification record into PDF files before returning them to the viewer.
    // Keep this opt-in so trainer/student previews retain the fast signed URL
    // path and only approved institution-wide evidence is stamped.
    if (stamped) return getServerPreviewUrl(filePath);

    // Reuse the same signed URL route that previously powered downloads. The
    // iframe can render this URL directly, avoiding a second full-file fetch
    // into a Blob and making preview loading substantially faster.
    try {
      const signedUrl = await this.getFileUrl(filePath);
      if (signedUrl && !/^(blob|data):/i.test(signedUrl)) return signedUrl;
    } catch (_) { /* continue with authenticated and server-side fallbacks */ }

    // Fetch the object through the authenticated Storage client first. This
    // avoids signed-URL policies turning an otherwise readable object into a
    // misleading "Object not found" response and gives the browser a clean
    // inline Blob URL.
    if (!/^https?:\/\//i.test(String(filePath)) || isSupabaseStorageUrl(filePath)) {
      const originalPath = String(filePath).trim();
      const normalizedPath = normalizeEvidencePath(originalPath);
      const candidatePaths = [...new Set([
        isSupabaseStorageUrl(originalPath) ? normalizedPath : originalPath,
        normalizedPath,
      ].filter(path => path && !/^https?:\/\//i.test(path)))];
      for (const candidatePath of candidatePaths) {
        const { data: sourceBlob, error } = await supabase.storage
          .from(EVIDENCE_BUCKET)
          .download(candidatePath);
        if (!error && sourceBlob) return createInlineObjectUrl(sourceBlob, candidatePath);
      }
    }

    let signedUrl;
    try {
      signedUrl = await this.getFileUrl(filePath);
    } catch (storageError) {
      // The Edge Function validates the caller and approved evidence, then
      // reads the private object with the service role. This is a safe fallback
      // for older Storage policies and legacy file-path formats.
      try {
        return await getServerPreviewUrl(filePath);
      } catch (fallbackError) {
        throw fallbackError || storageError;
      }
    }
    if (!signedUrl || /^(blob|data):/i.test(signedUrl)) return signedUrl;

    // Serve the fetched upload from a Blob URL so the browser's built-in PDF
    // or image viewer handles it inline, even when the stored object has an
    // attachment/octet-stream content disposition.
    try {
      const response = await fetch(signedUrl, { credentials: 'omit' });
      if (!response.ok) return getServerPreviewUrl(filePath);
      const sourceBlob = await response.blob();
      return createInlineObjectUrl(sourceBlob, filePath);
    } catch (_) {
      try {
        return await getServerPreviewUrl(filePath);
      } catch (_) {
        // Keep the signed URL as a fallback for environments that block a
        // cross-origin fetch even though the signed URL itself is accessible.
        return signedUrl;
      }
    }
  }
};

export default poeService;
